/**
 * 業務フロー図が「どの操作を開いた状態で出すか」を受け取るための検索条件。
 *
 * **外から特定の操作へ送るための入口である。** 業務ロジックの規則は
 * 「この規則はどの操作に効くか」を操作の名前で書いており、
 * 読み手はそこから操作の中身（説明・触るデータ・実装先）へ進みたい。操作は単体の詳細
 * ルートを持たず、中身を出しているのは業務フロー図の詳細ペインなので、行き先は「その操作が
 * 属するフロー図を、その操作を開いた状態で出す」になる。
 *
 * **指すのは節点 ID ではなく操作 ID である。** 節点 ID（`nodeId`）はフロー内の位置識別で、
 * 骨格を組み替えると変わる（決定 ah・ap）。外から指す鍵に位置識別を使うと、フローを直した
 * 瞬間に既存のリンクとブックマークが黙って別の節点を指す。操作は独立した集約で ID が動かない。
 *
 * **業務フローでは、図の中の選択そのものはここに写さない。** 図では制御節点（開始・
 * 分岐・終了）も選べるが、それらは操作を参照しないので載せる値が無い。選択を URL に写すと
 * 「操作を持つ節点のときだけ URL に出る」という非対称ができ、選択の表現が 2 通りに割れる。
 * そちらでここが答えるのは「どの操作を開いて始めるか」だけである。
 *
 * **`compare` は案件モードの左右比較を閉じたことだけを持つ。** 案件モードは既定で変更前と
 * 変更後の 2 面で開き、閉じたときだけ `?compare=0` を URL に残す。変更前と変更後に差が無い画面は
 * この値に関係なく 1 面で開く（差の有無で読み分けるのは `useCompareSearch`）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図が開いた状態で出す操作と、左右比較の開閉を、検索条件から読み書きする。
 * @graph-connects none
 */
import { parsePjSearch, searchValueOf, type PjSearch } from './pj-search.js';

/** 業務フロー図の検索条件。開く操作と、左右比較を閉じたことを指定したときだけ持つ。 */
export interface FlowSearch {
  /**
   * 開いた状態で出す操作の ID。数字だけの ID は数値、それ以外は文字列。未指定なら何も開かない。
   */
  op?: number | string;
  /** 案件モードの左右比較を閉じたときだけ `0`。未指定なら左右 2 面で開く。 */
  compare?: 0;
}

/**
 * 検索条件から開く操作の ID を読む。
 *
 * 空文字は指定なしと同じに畳む。手打ちの URL や古いブックマークから `?op=` だけが残った
 * 状態で来たときに、空の ID で節点を探しに行かないためである。
 *
 * **数字だけの ID は数値として届く。** 検索条件は JSON として読まれるので、`?op=1370` は
 * 文字列ではなく数値 1370 になる（`pj-search.ts` の案件番号と同じ）。数値を捨てると、手で書いた
 * `?op=1370` や共有されたリンクが黙って「何も開かない」になる。
 *
 * **数字だけの ID は数値のまま返す。** この戻り値はルーターが URL へ書き戻す値でもあり、
 * 文字列で返すと `?op=1370` が開いた瞬間に `?op=%221370%22` へ書き換わる（案件番号と同じ理由。
 * 規則は `searchValueOf`）。
 *
 * **鍵は必ず自分の判定結果で塗り替える。** 検証器の戻り値は検証器を持たない親ルートの生の
 * 検索条件へ重ねられるので、鍵ごと落とすと生の値がそのまま画面へ届く（`pj-search.ts` に
 * 同じ理由の記述がある）。
 *
 * @param raw 生の検索条件
 * @returns 業務フロー図の検索条件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件から開く操作の ID を読み、数字だけの ID は数値のまま、成立しない値は指定なしへ畳む。
 * @graph-connects searchValueOf [calls] 検索条件に置く形へ直す
 */
export function parseFlowSearch(raw: Record<string, unknown>): FlowSearch {
  const value = raw['op'];
  const text =
    typeof value === 'number' && Number.isFinite(value)
      ? String(value)
      : typeof value === 'string'
        ? value.trim()
        : '';
  return {
    op: text === '' ? undefined : searchValueOf(text),
    compare: parseCompare(raw['compare']),
  };
}

/**
 * 検索条件の `compare` を読む。`0` のときだけ「左右比較を閉じた」と読む。
 *
 * **既定は左右 2 面で、閉じたことだけを URL に書く。** 案件モードで AS-IS が見えないと
 * 感じる原因は、比較が既定で閉じていることにあった（2026-09-25 に決め直した）。開いた状態を
 * 既定にし、閉じた状態を URL に残せば、閉じた画面を共有した相手にも閉じた姿で届く。ただし
 * 変更前と変更後に差が無い画面では、この既定は効かず 1 面になる（2026-09-29。`useCompareSearch`）。
 * 数字だけの値は数値として届く（`?compare=0` は数値 0）が、手で書いた文字列の `'0'` も同じに読む。
 *
 * @param value 生の検索条件の値
 * @returns 閉じたなら 0。それ以外は指定なし
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件から、案件モードの左右比較を閉じたかどうかを読む。
 * @graph-connects none
 */
function parseCompare(value: unknown): 0 | undefined {
  return value === 0 || value === '0' ? 0 : undefined;
}

/**
 * 検索条件から、左右比較を開いているかを読む。閉じたと書かれていなければ開いている。
 *
 * @param search 検証済みの検索条件
 * @returns 左右 2 面で出すなら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件から、案件モードの左右比較を開いているかを読む。
 * @graph-connects none
 */
export function isCompareOpen(search: { compare?: unknown }): boolean {
  return search.compare !== 0;
}

/**
 * 今の検索条件に、左右比較の開閉だけを差し替えて返す。開くなら `compare` を外す。
 *
 * **検索条件を丸ごと置き換えない。** 同じ URL には案件（`?pj=`）と開く操作（`?op=`）も載る。
 * 開閉を写すたびに検索条件を作り直すと、比較を 1 回押しただけで案件が外れ、画面がマスターへ落ちる。
 *
 * @param prev 今の検索条件
 * @param open 左右 2 面で出すか
 * @returns 開閉だけを差し替えた検索条件
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 検索条件の左右比較の開閉だけを差し替え、案件や開く操作などほかの条件を残す。
 * @graph-connects none
 */
export function withCompare<T extends object>(prev: T, open: boolean): T & FlowSearch {
  return { ...prev, compare: open ? undefined : 0 };
}

/**
 * 業務のデータの流れの検索条件を読む — 案件（`pj`）と左右比較の開閉（`compare`）。
 *
 * **開く処理（`op`）は受けない。** 図の中の選択は URL に写さない（押したものを光らせるだけの
 * 見え方の切り替えである）ので、受けると「渡せるが読まれない」鍵ができる。
 *
 * @param raw 生の検索条件
 * @returns 案件と左右比較の開閉
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business データの流れの検索条件から、案件と左右比較の開閉を読み、成立しない値を指定なしへ畳む。
 * @graph-connects parsePjSearch [calls] 検索条件から案件番号を読む
 */
export function parseDataflowSearch(
  raw: Record<string, unknown>,
): PjSearch & Pick<FlowSearch, 'compare'> {
  return { ...parsePjSearch(raw), compare: parseCompare(raw['compare']) };
}
